import type { AgentDeployChatWidget } from "./agent-deploy-chat-widget.schema";
import type { AgentDeployTelegram } from "./agent-deploy-telegram.schema";
import type { AgentDeployWhatsappBaileys } from "./agent-deploy-whatsapp-baileys.schema";
import type { AgentDeployWhatsapp } from "./agent-deploy-whatsapp.schema";
import type { PulseMessage, ToolErrorInfo } from "./thread.schema";
import { z } from "zod";
import { $t } from "@/services/i18n";
import { hostnameValidator, outboundCallerIdValidator } from "@/utils/zod-validations";
import { AgentDeployChatWidgetSchema } from "./agent-deploy-chat-widget.schema";
import { agentDeployWhatsappSchema } from "./agent-deploy-whatsapp.schema";
import { KEYTERM_MAX_TERM_CHARS, KEYTERM_MAX_TERMS, KEYTERM_MAX_TOTAL_CHARS } from "./keyterm-limits";

export const MCPServerSchema = z
  .object({
    name: z.string().min(1),
    disabled: z.boolean().default(false),
    connection_id: z.string().optional(),
    url: z.url().optional(),
    transport: z.enum(["auto", "http", "sse", "websocket"]).optional(),
    headers: z.record(z.string(), z.string()).optional(),
    tools: z
      .array(
        z.object({
          name: z.string(),
          description: z.string().optional(),
          parameters: z.record(z.string(), z.any()).optional(),
          disabled: z.boolean().default(false),
        })
      )
      .optional(),
  })
  .refine(
    (server) => Boolean(server.connection_id) || Boolean(server.url),
    { message: "mcp_server.url_required", path: ["url"] },
  );

export type MCPServer = z.infer<typeof MCPServerSchema>;

export const FunctionCallSchema = z.object({
  name: z.string().min(1),
  description: z.string().min(1),
  parameters: z.record(z.string(), z.any()),
});

export const responseTypeOptions = ["json", "html", "text", "xml", "csv"] as const;

const ApiSchema = z.strictObject({
    url: z.string().min(1),
    mode: z.union([z.literal("query"), z.literal("body"), z.literal("param")]),
    method: z.string(),
    headers: z.record(z.string(), z.string()).optional(),
    query: z.record(z.string(), z.string()).optional(),
    body: z.record(z.string(), z.string()).optional(),
    response_type: z.enum([...responseTypeOptions, "auto"]).optional(),
  });

export const ApiToolSchema = FunctionCallSchema.extend({
  api: ApiSchema,
});

const RagToolSchema = z.object({
  name: z.string(),
  description: z.string(),
  collection_id: z.string().optional(),
  collection_name: z.string(),
  filename: z.string(),
  n: z.number().optional(),
  chunk_size: z.number().optional(),
  embedding_model: z.string().optional(),
  chunk_overlap: z.number().optional(),
  separators: z.string().optional(),
});

// Source of truth for valid internal tool names is the server catalog
// (GET /agents/internal-tools). This enum intentionally keeps accepting the
// deprecated names (browser, code_interpreter) so legacy agents still validate;
// the picker hides deprecated entries for new selection instead of dropping them.
const InternalToolSchema = z.object({
  name: z.enum(["current_datetime", "browser", "calculator", "code_interpreter", "code_execution", "web_fetch", "web_search"]),
  params: z.unknown().optional(),
});

// One entry of the server catalog (GET /agents/internal-tools). The catalog is
// the source of truth: name/description/flags come from the server, the client
// only falls back to a static list when the endpoint is unreachable.
export const InternalToolCatalogEntrySchema = z.object({
  name: z.string().min(1),
  description: z.string(),
  deprecated: z.boolean().optional(),
  aliasOf: z.string().optional(),
  alwaysAvailable: z.boolean().optional(),
});

export type InternalToolCatalogEntry = z.infer<typeof InternalToolCatalogEntrySchema>;

export const AgentFunctionToolSchema = z.union([FunctionCallSchema, ApiToolSchema]);

const BaseKnowledgeToolSchema = z.object({
  name: z.string(),
  additional_instructions: z.string().optional(),
  collection_id: z.string().optional(),
  collection_name: z.string().min(1),
});

const KnowledgeToolRetrieveSchema = BaseKnowledgeToolSchema.extend({
  action: z.literal("retrieve"),
  n_results: z.number().optional(),
});

const KnowledgeToolAddSchema = BaseKnowledgeToolSchema.extend({
  action: z.literal("add"),
});

export const KnowledgeToolSchema = z.union([KnowledgeToolRetrieveSchema, KnowledgeToolAddSchema]);

export type KnowledgeTool = z.infer<typeof KnowledgeToolSchema>;

export const MCPServerToolSchema = z.object({
  name: z.string(),
  description: z.string(),
  parameters: z.record(z.string(), z.any()),
  mcp_server: MCPServerSchema,
});

export const AgentToolSchema = z.union([
  z.object({
    type: z.enum(["function_call"]),
    tool: FunctionCallSchema,
    disabled: z.boolean().nullish(),
  }),
  z.object({
    type: z.literal("api"),
    tool: ApiToolSchema,
    disabled: z.boolean().nullish(),
  }),
  z.object({
    type: z.literal("internal"),
    tool: InternalToolSchema,
    disabled: z.boolean().nullish(),
  }),
  z.object({
    type: z.literal("rag"),
    tool: RagToolSchema,
    disabled: z.boolean().nullish(),
  }),
  z.object({
    type: z.literal("knowledge"),
    tool: KnowledgeToolSchema,
    disabled: z.boolean().nullish(),
  }),
  z.object({
    type: z.literal("mcp"),
    tool: MCPServerToolSchema,
    disabled: z.boolean().nullish(),
  }),
]);

export type FunctionTool = z.infer<typeof FunctionCallSchema>;
export type RagTool = z.infer<typeof RagToolSchema>;
export type ApiTool = z.infer<typeof ApiToolSchema>;
export type InternalTool = z.infer<typeof InternalToolSchema>;
export type AgentFunctionTool = z.infer<typeof AgentFunctionToolSchema>;
export type AgentTool = z.infer<typeof AgentToolSchema>;

// ==================== GREETING CONFIGURATION ====================

/**
 * Fixed greeting - always uses the same predefined message
 */
const FixedGreetingSchema = z.object({
  type: z.literal("fixed"),
  message: z.string().min(1), // Validation message handled in UI
  allow_interruptions: z.boolean().default(false),
});

/**
 * Generated greeting - always dynamically generates greeting using AI
 */
const GeneratedGreetingSchema = z.object({
  type: z.literal("generated"),
  generation_context: z.string().optional(),
  include_thread_summary: z.boolean().default(true),
});

/**
 * Contextual greeting - smart decision based on conversation history
 * Generates if history exists, otherwise uses fixed message
 */
const ContextualGreetingSchema = z.object({
  type: z.literal("contextual"),
  fixed_message: z.string().min(1), // Validation message handled in UI
  generation_context: z.string().optional(),
  include_thread_summary: z.boolean().default(true),
  allow_interruptions: z.boolean().default(false),
});

/**
 * Discriminated union for greeting configuration
 * Supports three strategies: fixed, generated, and contextual
 */
export const GreetingConfigSchema = z.discriminatedUnion("type", [
  FixedGreetingSchema,
  GeneratedGreetingSchema,
  ContextualGreetingSchema,
]);

export type GreetingConfig = z.infer<typeof GreetingConfigSchema>;

// ==================== CALL CONFIGURATION ====================

export const CallConfigurationSchema = z.object({
  initial_message: z.string().optional(), // LEGACY - kept for backward compatibility
  additional_instructions: z.string().optional(),
  variables: z.record(z.string(), z.string()).optional(),
  greeting: GreetingConfigSchema, // NEW - required greeting configuration
  inactivity_timeout: z.object({
    enabled: z.boolean().optional(),
    time_in_seconds: z.number().min(0).optional(),
    message: z.string().optional(),
    presence_attempts: z.number().min(0).max(5).default(0),
    presence_message: z.string().optional(),
  }),
  max_duration: z.object({
    time_in_minutes: z.number().min(0).max(60).optional(),
    message: z.string().optional(),
  }),
  thinking_sound: z
    .object({
      enabled: z.boolean().default(true),
      audio: z.string().nullable().default("thinking_1"),
      volume: z.number().min(0).max(1).default(0.3),
    })
    .optional(),
  ambient_sound: z
    .object({
      enabled: z.boolean().default(false),
      audio: z.string().nullable().default("ambient_1"),
      volume: z.number().min(0).max(1).default(0.8),
    })
    .optional(),
  tools: z
    .object({
      end_dialog: z
        .object({
          enabled: z.boolean().default(false),
          description: z.string().optional(),
          json_schema: z.record(z.string(), z.any()).optional(),
        })
        .optional(),
      transfer_call: z
        .object({
          enabled: z.boolean().default(false),
          description: z.string().optional(),
          destinations: z.array(z.string()).optional(),
          timeout: z.number().min(1).max(15).optional(), // Fixed per agent - the LLM does not override this in the tool call
        })
        .optional(),
      send_dtmf: z
        .object({
          enabled: z.boolean().default(false),
          description: z.string().optional(),
        })
        .optional(),
      receive_dtmf: z
        .object({
          enabled: z.boolean().default(false),
          description: z.string().optional(),
          default_timeout: z.number().min(1).max(60).optional(), // LLM defines all other params
        })
        .optional(),
    })
    .optional(),
});

// ==================== CHAT CONFIGURATION ====================

export const ChatConfigurationSchema = z.object({
  tools: z
    .object({
      escalate_to_human: z
        .object({
          enabled: z.boolean().default(false),
          description: z.string().optional(),
          max_wait_minutes: z.number().int().min(60).max(1440).default(1440),
          contacts: z
            .array(
              z.object({
                type: z.enum(["email"]),
                value: z.email(),
              })
            )
            .optional(),
        })
        .optional(),
    })
    .optional(),
});

export type ChatConfiguration = z.infer<typeof ChatConfigurationSchema>;

// Messages are functions so $t runs at validation time. A module-scope $t here
// hits the i18n import cycle in TDZ and breaks the app on load.
const KeytermSchema = z
  .array(
    z
      .string()
      .trim()
      .min(1)
      .max(KEYTERM_MAX_TERM_CHARS, {
        error: () => $t("agents:keyterm_max_term_chars", { max: KEYTERM_MAX_TERM_CHARS }),
      })
  )
  .max(KEYTERM_MAX_TERMS, { error: () => $t("agents:keyterm_max_terms", { max: KEYTERM_MAX_TERMS }) })
  .refine(terms => terms.reduce((sum, t) => sum + t.length, 0) <= KEYTERM_MAX_TOTAL_CHARS, {
    error: () => $t("agents:keyterm_max_total_chars", { max: KEYTERM_MAX_TOTAL_CHARS }),
  });

export const AgentSchema = z.strictObject({
    id: z.string(),
    organization_id: z.string(),
    project_id: z.string().nullish(),
    user_id: z.string(),
    name: z.string().min(1),
    description: z.string().min(1).max(255),
    instructions: z.string().nullish(),
    llm_model: z.string(),
    tts_model: z.string().min(1),
    tts_voice: z.string().min(1),
    language: z.string().min(1),
    keyterm: KeytermSchema.nullish(),
    tools: z.array(AgentToolSchema).default([]),
    call_configuration: CallConfigurationSchema.nullish(),
    chat_configuration: ChatConfigurationSchema.nullish(),
    tts_params: z.record(z.string(), z.any()).nullish(),
    parameters: z.record(z.string(), z.any()).optional(),
    settings: z.object({
      tool_search_enabled: z.boolean().optional(),
      tool_search_threshold: z.number().optional(),
      max_steps: z.number().int().min(2).max(50).optional(),
      tool_prune_preserve_last: z.number().int().min(1).max(20).optional(),
      tool_catalog_limit: z.number().int().min(1).max(100).optional(),
      tool_output_max_chars: z.number().int().min(1000).max(200000).optional(),
    }).nullish(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
    share: z.enum(["user", "organization", "link", "public"]).default("organization"),
    post_analysis: z.array(z.string()).optional(),
    mcp_servers: z.array(MCPServerSchema).optional(),
    deployments: z.object({
      sip: z.any().nullish(),
      whatsapp: z.any().nullish(),
      whatsappBaileys: z.any().nullish(),
      chatWidget: z.any().nullish(),
      telegram: z.any().nullish(),
    }).optional(),
  });

export type CallConfiguration = z.infer<typeof CallConfigurationSchema>;

export const AgentBodySchema = AgentSchema.omit({
  id: true,
  organization_id: true,
  user_id: true,
  created_at: true,
  updated_at: true,
}).extend({
  parameters: z.record(z.string(), z.any()).nullish(),
  tools: z.array(AgentToolSchema),
});

export const AgentImportSchema = AgentBodySchema.extend({
  description: z.string().max(255).optional(),
});

/**
 * @deprecated Use CallConfiguration instead. VoiceAgentConfiguration is now an alias to CallConfiguration.
 * All voice configurations (SIP, Voice Playground, WhatsApp) use the same schema.
 * This type will be removed in a future version.
 */
export const VoiceAgentConfigurationSchema = CallConfigurationSchema;

/**
 * @deprecated Use CallConfiguration instead. SipCallConfiguration is now an alias to CallConfiguration.
 * This type will be removed in a future version.
 */
export const SipCallConfigurationSchema = CallConfigurationSchema;

/**
 * @deprecated Use CallConfiguration instead. This type will be removed in a future version.
 */
export type SipCallConfiguration = z.infer<typeof SipCallConfigurationSchema>;

/**
 * @deprecated Use CallConfiguration instead. This type will be removed in a future version.
 */
export type VoiceAgentConfiguration = z.infer<typeof VoiceAgentConfigurationSchema>;

// Schema para configuração de outbound trunk (apenas conexão SIP)
export const OutboundConfigSchema = z.object({
  address: hostnameValidator,
  phone_numbers: z.array(outboundCallerIdValidator).superRefine((values, ctx) => {
    if (values.length === 0) {
      ctx.addIssue({
        code: "too_small",
        minimum: 1,
        origin: "array",
        inclusive: true,
        message: $t("sip_trunk.validation.at_least_one_number_required"),
      });
    }
  }),
  auth_username: z.string().optional(),
  auth_password: z.string().optional(),
});

// Schema for external SIP registration (register with external servers to receive calls)
export const ExternalSipRegistrationSchema = z.object({
  sip_server: hostnameValidator,
  sip_username: z.string().min(1),
  sip_password: z.string().min(1),
});

export const AgentDeploySipSchema = z.object({
  agent_id: z.string(),
  organization_id: z.string(),
  enable_inbound: z.boolean(),
  enable_outbound: z.boolean().optional(),
  agent_metadata: VoiceAgentConfigurationSchema.optional(),
  outbound_configuration: OutboundConfigSchema.optional(),
  did_id: z.string().nullish(),
  trunk_id: z.string().optional(),
  outbound_trunk_id: z.string().optional(),
  dispatch_rule_id: z.string().optional(),
  // External SIP registration fields
  sip_server: z.string().nullish(),
  sip_username: z.string().nullish(),
  sip_password: z.string().nullish(),
  created_at: z.string().nullish(),
  updated_at: z.string().nullish(),
});

export const AgentWithDeploysSchema = AgentSchema.extend({
  agent_deploy_whatsapp: agentDeployWhatsappSchema.optional(),
  agent_deploy_chat_widget: AgentDeployChatWidgetSchema.optional(),
  agent_deploy_sip: AgentDeploySipSchema.optional(),
});

export type Agent = z.infer<typeof AgentSchema>;

/**
 * @deprecated Use Agent + separate deployment queries instead (see deployments.query.ts)
 * This type couples agents with their deployments which causes performance issues
 * and makes cache invalidation difficult. Use Agent type and fetch deployments separately.
 * Will be removed in a future version.
 */
export type AgentWithDeploys = z.infer<typeof AgentWithDeploysSchema>;

/**
 * Helper type for composing agents with deployments reactively
 * Use this in computed properties after fetching agents and deployments separately
 *
 * @example
 * ```ts
 * const agentsQuery = useAgentsQuery();
 * const deploymentsQueries = useDeploymentsQueries();
 *
 * const agentsWithDeployments = computed<AgentWithDeployments[]>(() => {
 *   const agents = agentsQuery.data.value ?? [];
 *   const sips = deploymentsQueries.sip.data.value ?? [];
 *   // ... compose data
 * });
 * ```
 */
export type AgentWithDeployments = Agent & {
  deployments: {
    sip?: AgentDeploySip;
    whatsapp?: AgentDeployWhatsapp;
    whatsappBaileys?: AgentDeployWhatsappBaileys;
    chatWidget?: AgentDeployChatWidget;
    telegram?: AgentDeployTelegram;
  };
};

export type AgentBody = z.infer<typeof AgentBodySchema>;
export type AgentImport = z.infer<typeof AgentImportSchema>;

export const FunctionSchema = z.object({
  name: z.string(),
  description: z.string(),
  parameters: z.string().superRefine((val, ctx) => {
    try {
      JSON.parse(val);
    } catch (_e: unknown) {
      ctx.addIssue({
        code: "custom",
        message: "Invalid JSON",
      });
    }
  }),
  api: ApiSchema.optional(),
});

export type AgentJSONFunction = z.infer<typeof FunctionSchema>;

export const AgentFileBodySchema = z.object({
  name: z.string(),
  description: z.string(),
  files: z.array(z.any()).min(1),
  chunk_size: z.number().optional(),
  chunk_overlap: z.number().optional(),
  separators: z.string().optional(),
  n: z.number().optional(),
  embedding_model: z.string().optional(),
});

export type AgentFileBody = z.infer<typeof AgentFileBodySchema>;

export const AgentFileSchema = z.object({
  id: z.string(),
  agent_id: z.string().optional(),
  organization_id: z.string().optional(),
  file_info: z.object({
    encoding: z.string(),
    filename: z.string(),
    mimetype: z.string(),
  }),
  collection_name: z.string(),
  size: z.number(),
  type: z.string(),
  status: z.string().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
});

export type AgentFile = z.infer<typeof AgentFileSchema>;

export interface CompletionUsage {
  completion_tokens: number;
  prompt_tokens: number;
  total_tokens: number;
  /**
   * AI-1177: the public cache breakdown, present only when the provider
   * reported it. The gateway speaks input/output outwards even though the
   * token counts above kept the provider's own spelling.
   */
  input_tokens_details?: {
    cached_tokens?: number;
    cache_write_tokens?: number;
  };
}

interface ToolCallStartEvent {
  id: string;
  event: "tool_call_initiated";
  tool_calls: {
    function: {
      name: string;
      arguments: string;
    };
    id: string;
    type: string;
  }[];
  content: string | null;
  name: string;
}

interface ToolCallEndEvent {
  event: "tool_call_complete";
  result: any;
  tool_call_id: string;
  tool_call_name?: string;
  /** AI-1179: mirrors the persisted tool history entry. */
  status?: "success" | "error";
  error?: ToolErrorInfo;
}

interface OnMessageEvent {
  id: string;
  event: "message_chunk";
  content: string;
}

interface MessageEndEvent {
  id: string;
  event: "message_complete";
  content: string;
}

interface AbortEvent {
  event: "abort";
  reason: any;
}

interface MessageReport {
  event: "message_report";
  id: string;
  usage: CompletionUsage;
  execution_time: number;
  // Durations in ms next to execution_time. Absent for older threads and
  // non-streaming runs.
  /** ms until the first streamed token (content or reasoning) of this model call. */
  time_to_first_token?: number;
  /** ms from the first streamed token of this model call to this report. */
  generation_time?: number;
  /** Only when the provider reported it. Never a synthesized zero. */
  reasoning_tokens?: number;
  /** Duration of the reasoning block of this model call, in ms. */
  reasoning_duration_ms?: number;
}

/** Opens a reasoning block. `id` is the run id of the model call. */
interface ReasoningStartEvent {
  event: "reasoning_start";
  id: string;
}

/** Reasoning text as it streams. Absent for providers that expose no text. */
interface ReasoningChunkEvent {
  event: "reasoning_chunk";
  id: string;
  content: string;
}

interface ReasoningEndEvent {
  event: "reasoning_end";
  id: string;
  /** Provider message id of the assistant message the reasoning belongs to. */
  message_id?: string;
  duration_ms: number;
  tokens?: number;
}

interface TotalReport {
  event: "total_report";
  usage: CompletionUsage;
  execution_time: number;
  messages: PulseMessage[];
}

interface AgentError {
  event: "error";
  error: unknown;
}

export type AgentEvent =
  | ToolCallStartEvent
  | ToolCallEndEvent
  | OnMessageEvent
  | MessageEndEvent
  | AbortEvent
  | MessageReport
  | TotalReport
  | AgentError
  | ReasoningStartEvent
  | ReasoningChunkEvent
  | ReasoningEndEvent;

// Schema para presets SIP
export const SipPresetSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  type: z.enum(["inbound", "outbound"]),
  is_default: z.boolean(),
  configuration: OutboundConfigSchema,
  created_at: z.string(),
});

export const AgentDeploySipBodySchema = z.object({
  enable_inbound: z.boolean().optional(),
  enable_outbound: z.boolean().optional(),
  // agent_metadata is now optional - call settings are in agent.call_configuration
  // agent_metadata: VoiceAgentConfigurationSchema.optional(),
  did_id: z.string().nullish(),
  outbound_configuration: OutboundConfigSchema.nullish(),
});

// Schema para overrides de metadata (usa VoiceAgentConfiguration como base).
// Tools sao capacidade do agente, nao da chamada: omitido antes do partial()
// para que o tipo inferido nem exponha o campo.
export const MetadataOverridesSchema = VoiceAgentConfigurationSchema.omit({
  tools: true,
}).partial();

// Schema para fazer chamada outbound
export const OutboundCallBodySchema = z.object({
  phone_number: z.string().min(1),
  initial_message: z.string().optional(),
  additional_instructions: z.string().optional(),
  metadata_overrides: MetadataOverridesSchema.optional(),
  config_overrides: MetadataOverridesSchema.optional(),
});

// Schema extendido para uso interno no formulário
export const OutboundCallFormSchema = OutboundCallBodySchema.extend({
  customize_call: z.boolean().default(false),
});

// Schema para resposta de chamada outbound
export const OutboundCallResponseSchema = z.object({
  success: z.boolean(),
  callId: z.string(),
  roomName: z.string(),
  participantId: z.string(),
});

export type AgentDeploySip = z.infer<typeof AgentDeploySipSchema>;
export type AgentDeploySipBody = z.infer<typeof AgentDeploySipBodySchema>;
export type AgentDeployConfig = z.infer<typeof VoiceAgentConfigurationSchema>;
export type OutboundConfig = z.infer<typeof OutboundConfigSchema>;
export type SipPreset = z.infer<typeof SipPresetSchema>;
export type MetadataOverrides = z.infer<typeof MetadataOverridesSchema>;
export type OutboundCallBody = z.infer<typeof OutboundCallBodySchema>;
export type OutboundCallForm = z.infer<typeof OutboundCallFormSchema>;
export type OutboundCallResponse = z.infer<typeof OutboundCallResponseSchema>;
